// The summary and record-list table: formatted cells, a grand total for measures that can be added up,
// client-side column sorting and paged rows, so a 10,000-row result never lands in the DOM at once.

import type { QueryDisplay, QueryResult, QueryResultColumn } from './types';
import { clipsKind, columnKind, columnWidthCh, enhanceAnalyticsTable, isIdentityColumn, resizeHandleMarkup, savedColumnWidthCh, tableWidthCh } from './analytics-table';
import { displayCell, escapeHtml, isValueColumn, resolveFormat } from './format';
import { axisComparator } from './pivot-table';
import { serverTotals, totalCell, type TotalOptions } from './result-totals';

export const TABLE_PAGE_SIZE = 100;

export interface TableView {
    /** Row indexes in display order (after sorting); defaults to result order. */
    order?: number[];
    start?: number;
    pageSize?: number;
    sort?: { alias: string; dir: 'asc' | 'desc' } | null;
    /** False for dataset measures whose group values must not be added again. */
    reaggregable?: TotalOptions['reaggregable'];
}

function cellClass(format: ReturnType<typeof resolveFormat>, extra: string[] = []): string {
    const classes = [format.align === 'end' ? 'text-right tabular-nums' : format.align === 'center' ? 'text-center' : '',
        format.wrap ? 'analytics-cell--wrap' : '', ...extra].filter(Boolean).join(' ');
    return classes ? ` class="${classes}"` : '';
}

function cellMeta(column: QueryResultColumn, result: QueryResult, index: number): string {
    const kind = columnKind(column);
    const freeze = isIdentityColumn(result, index) ? ' data-freeze="true"' : '';
    return ` data-kind="${escapeHtml(kind)}"${freeze}`;
}

/**
 * Grand totals for a grouped result, as display text per measure alias: the engine's when sent (money in several
 * currencies is listed per currency), otherwise only measures that can be combined on the client.
 */
export function tableTotals(result: QueryResult, display: QueryDisplay | null = null, reaggregable?: TotalOptions['reaggregable']): { cells: Record<string, string>; skipped: QueryResultColumn[] } | null {
    if (result.reportType === 'recordList' || result.reportType === 'funnel' || result.rows.length < 2) return null;
    const measures = result.columns.filter(c => c.kind === 'measure');
    if (!measures.length || !result.columns.some(c => c.kind === 'dimension')) return null;
    const grand = serverTotals(result)?.grand ?? [];
    // A partial result cannot be totalled on the client, and averages or distinct counts never add up.
    const options: TotalOptions = { display, clientTotals: !result.truncated, reaggregable };
    const cells: Record<string, string> = {}; const skipped: QueryResultColumn[] = [];
    for (const measure of measures) {
        const cell = totalCell(measure, grand.length ? grand : null, result.rows, options);
        if (cell.known) cells[measure.alias] = cell.text; else skipped.push(measure);
    }
    // The engine's totals are shown even when none can be one number, so the footer says which are not totalled.
    return Object.keys(cells).length || grand.length ? { cells, skipped } : null;
}

/** Row order for a column sort: the column's natural order, missing values last. */
export function sortedOrder(result: QueryResult, alias: string, dir: 'asc' | 'desc'): number[] {
    const column = result.columns.find(c => c.alias === alias);
    const indexes = result.rows.map((_, i) => i);
    if (!column) return indexes;
    const compare = axisComparator(column.kind === 'measure' ? { ...column, dataType: 'number' } : column, dir);
    return indexes.sort((a, b) => compare(result.rows[a][alias], result.rows[b][alias]) || a - b);
}

/** Table rows: the platform's typed cell markup when the server sent it, otherwise the formatted value. */
export function tableMarkup(result: QueryResult, display: QueryDisplay | null = null, view: TableView = {}): string {
    display ??= result.display ?? null;
    const cols = result.columns
        .map((c, index) => `<col data-kind="${escapeHtml(columnKind(c))}"${isIdentityColumn(result, index) ? ' data-freeze="true"' : ''}${savedColumnWidthCh(c, display) != null ? ` data-width-ch="${savedColumnWidthCh(c, display)}"` : ''}>`)
        .join('');
    const head = result.columns
        .map((c, index) => {
            const format = resolveFormat(c, display, { valueColumn: isValueColumn(c, result.reportType) });
            const label = escapeHtml(c.label ?? c.alias);
            const sorted = view.sort?.alias === c.alias ? ` aria-sort="${view.sort.dir === 'asc' ? 'ascending' : 'descending'}"` : '';
            return `<th scope="col"${cellClass(format)}${cellMeta(c, result, index)}${sorted}><span class="analytics-th__label">${label}</span>${resizeHandleMarkup(label, columnWidthCh(c, display))}</th>`;
        })
        .join('');
    const order = view.order ?? result.rows.map((_, i) => i);
    const start = Math.max(0, view.start ?? 0);
    const pageSize = view.pageSize ?? TABLE_PAGE_SIZE;
    const body = order.slice(start, start + pageSize)
        .map(index => {
            const row = result.rows[index];
            const typed = result.cellHtml?.[index];
            const cells = result.columns
                .map((c, columnIndex) => {
                    const context = { valueColumn: isValueColumn(c, result.reportType), row };
                    const format = resolveFormat(c, display, context);
                    const kind = columnKind(c);
                    const markup = typed?.[c.alias];
                    const text = markup ?? escapeHtml(displayCell(row[c.alias], c, display, context));
                    const inner = !markup && clipsKind(kind, format.wrap)
                        ? `<span class="analytics-cell-clip" tabindex="0">${text}</span>`
                        : text;
                    return `<td${cellClass(format)}${cellMeta(c, result, columnIndex)}>${inner}</td>`;
                })
                .join('');
            return `<tr>${cells}</tr>`;
        })
        .join('');
    const totals = tableTotals(result, display, view.reaggregable);
    const foot = totals ? `<tfoot><tr data-table-total>${result.columns.map((c, index) => {
        const format = resolveFormat(c, display, { valueColumn: isValueColumn(c, result.reportType) });
        if (index === 0) return `<th scope="row"${cellClass(format)}>Total</th>`;
        if (c.kind !== 'measure') return '<td></td>';
        if (!(c.alias in totals.cells)) return `<td${cellClass(format)} data-total="unavailable"><span class="sr-only">Not totalled</span></td>`;
        return `<td${cellClass(format)} data-total="true">${escapeHtml(totals.cells[c.alias])}</td>`;
    }).join('')}</tr></tfoot>` : '';
    // The table's name is for assistive technology only; a visible caption appears only when a total needs explaining.
    const notes = [notTotalled(totals?.skipped.filter(c => !c.currencyAlias) ?? [], 'averages and distinct counts cannot be added up.'),
        notTotalled(totals?.skipped.filter(c => !!c.currencyAlias) ?? [], 'the amounts are in more than one currency.')].filter(Boolean).join(' ');
    const caption = notes ? `<caption><span class="sr-only">Report data. </span>${escapeHtml(notes)}</caption>` : '<caption class="sr-only">Report data</caption>';
    return `
        <div class="analytics-table-wrap" tabindex="0" role="region" aria-label="Report data">
            <table class="analytics-table" data-width-ch="${tableWidthCh(result, display)}">
                ${caption}
                <colgroup>${cols}</colgroup>
                <thead><tr>${head}</tr></thead>
                <tbody>${body}</tbody>${foot}
            </table>
        </div>${order.length > pageSize ? pagerMarkup(start, pageSize, order.length) : ''}`;
}

function notTotalled(columns: QueryResultColumn[], reason: string): string {
    return columns.length ? `${columns.map(c => c.label ?? c.alias).join(', ')} ${columns.length > 1 ? 'are' : 'is'} not totalled: ${reason}` : '';
}

function pagerMarkup(start: number, size: number, total: number): string {
    const end = Math.min(start + size, total);
    return `<div class="analytics-table-pager flex items-center gap-2 flex-wrap mt-2" role="group" aria-label="Table rows">
        <button type="button" class="sp-button sp-button--neutral sp-button--sm" data-table-page="previous" aria-label="Previous rows" ${start === 0 ? 'disabled' : ''}>Previous</button>
        <span role="status" aria-live="polite">Rows ${(start + 1).toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}</span>
        <button type="button" class="sp-button sp-button--neutral sp-button--sm" data-table-page="next" aria-label="Next rows" ${end >= total ? 'disabled' : ''}>Next</button>
    </div>`;
}

/**
 * Paint the table and make it interactive: headers sort (keyboard and pointer), rows page, columns resize.
 * Hosts without a DOM (string-only test hosts) still receive the first page's markup.
 * @param state The page position and sort to start from, updated as the reader pages and sorts. A host that
 *   repaints the same result (the builder after a resize or a display change) passes the same object back so the
 *   reader stays on their page; a new result starts from a new object.
 */
export function renderSummaryTable(host: HTMLElement, result: QueryResult, display: QueryDisplay | null = null,
    onResize?: (alias: string, widthCh: number) => void, reaggregable?: TotalOptions['reaggregable'], state: TableView = {}): { destroy(): void } {
    const view = state;
    view.pageSize ??= TABLE_PAGE_SIZE; view.sort ??= null; view.reaggregable = reaggregable;
    // The display order follows the sort; it is worked out again if the rows are not the ones it was made for.
    if (view.sort && view.order?.length !== result.rows.length) view.order = sortedOrder(result, view.sort.alias, view.sort.dir);
    if (!view.sort) view.order = undefined;
    const total = result.rows.length;
    view.start = Math.max(0, Math.min(view.start ?? 0, Math.floor(Math.max(0, total - 1) / view.pageSize) * view.pageSize));
    let disposeResize = () => {};
    let disposed = false;
    const paint = (focus?: string): void => {
        disposeResize();
        host.innerHTML = tableMarkup(result, display, view);
        if (typeof host.querySelector !== 'function') return;
        const wrap = host.querySelector<HTMLElement>('.analytics-table-wrap');
        disposeResize = wrap ? enhanceAnalyticsTable(wrap, (index, width) => onResize?.(result.columns[index].alias, width)) : () => {};
        host.querySelectorAll<HTMLElement>('thead th .analytics-th__label').forEach((label, index) => {
            const column = result.columns[index];
            if (!column || result.rows.length < 2) return;
            label.setAttribute('role', 'button'); label.tabIndex = 0;
            const current = view.sort?.alias === column.alias ? view.sort.dir : null;
            label.setAttribute('aria-label', `${column.label ?? column.alias}. Sort ${current === 'asc' ? 'descending' : 'ascending'}`);
            label.dataset.sortAlias = column.alias;
            const activate = () => {
                if (disposed) return;
                const dir = view.sort?.alias === column.alias && view.sort.dir === 'asc' ? 'desc' : 'asc';
                view.sort = { alias: column.alias, dir }; view.order = sortedOrder(result, column.alias, dir); view.start = 0;
                paint(`[data-sort-alias="${CSS.escape(column.alias)}"]`);
            };
            label.addEventListener('click', activate);
            label.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); } });
        });
        host.querySelectorAll<HTMLButtonElement>('[data-table-page]').forEach(button => button.addEventListener('click', () => {
            if (disposed || button.disabled) return;
            const size = view.pageSize ?? TABLE_PAGE_SIZE;
            const total = view.order?.length ?? result.rows.length;
            view.start = Math.max(0, Math.min(Math.floor((total - 1) / size) * size, (view.start ?? 0) + (button.dataset.tablePage === 'next' ? size : -size)));
            paint(`[data-table-page="${button.dataset.tablePage}"]`);
        }));
        if (focus) {
            const target = host.querySelector<HTMLElement>(focus);
            (target && !(target as HTMLButtonElement).disabled ? target : host.querySelector<HTMLElement>('[data-table-page]:not([disabled])'))?.focus();
        }
    };
    paint();
    return { destroy() { disposed = true; disposeResize(); } };
}
